(() => {
  const states = new WeakMap();
  const pending = new Set();
  let frame = 0;

  // Measure the rendered text, not its character count: translations wrap differently.
  function measure(wrapper) {
    const state = states.get(wrapper);
    if (!state || !wrapper.isConnected) return;

    const { description, button } = state;
    const width = wrapper.getBoundingClientRect().width;
    if (width <= 0) return;

    const style = getComputedStyle(description);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5;
    const probe = description.cloneNode(true);

    probe.removeAttribute('id');
    probe.setAttribute('aria-hidden', 'true');
    probe.setAttribute('inert', '');
    Object.assign(probe.style, {
      position: 'absolute',
      inset: 'auto',
      left: '-100000px',
      top: '0',
      width: `${width}px`,
      display: 'block',
      visibility: 'hidden',
      pointerEvents: 'none',
      overflow: 'visible',
      height: 'auto',
      maxHeight: 'none',
      webkitLineClamp: 'unset'
    });

    wrapper.appendChild(probe);
    const collapsible = probe.getBoundingClientRect().height > lineHeight * 2 + 1;
    probe.remove();

    wrapper.classList.toggle('is-collapsible', collapsible);
    button.hidden = !collapsible;

    if (!collapsible) {
      wrapper.classList.remove('is-expanded');
      button.setAttribute('aria-expanded', 'false');
    }
  }

  function schedule(wrapper) {
    pending.add(wrapper);
    if (frame) return;

    frame = requestAnimationFrame(() => {
      frame = 0;
      pending.forEach(measure);
      pending.clear();
    });
  }

  function initialize(root = document) {
    root.querySelectorAll('.fendo-collection-description').forEach((wrapper) => {
      if (states.has(wrapper)) return;

      const description = wrapper.querySelector('.collection-description');
      const button = wrapper.querySelector('[data-fendo-collection-description-toggle]');
      if (!description || !button) return;

      const state = { description, button, width: -1, observer: null };
      states.set(wrapper, state);

      if ('ResizeObserver' in window) {
        state.observer = new ResizeObserver((entries) => {
          const width = entries[0].contentRect.width;
          // Ignore height changes caused by opening/closing the description.
          if (Math.abs(width - state.width) < 0.5) return;
          state.width = width;
          schedule(wrapper);
        });
        state.observer.observe(wrapper);
      }

      schedule(wrapper);
    });
  }

  function remeasure() {
    document.querySelectorAll('.fendo-collection-description').forEach(schedule);
  }

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-fendo-collection-description-toggle]')
      : null;
    if (!button || button.hidden) return;

    const wrapper = button.closest('.fendo-collection-description');
    if (!wrapper || !wrapper.classList.contains('is-collapsible')) return;

    const expanded = wrapper.classList.toggle('is-expanded');
    button.setAttribute('aria-expanded', String(expanded));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  window.addEventListener('resize', remeasure, { passive: true });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.fendo-collection-description').forEach((wrapper) => {
      states.get(wrapper)?.observer?.disconnect();
      pending.delete(wrapper);
      states.delete(wrapper);
    });
  });

  if (document.fonts) {
    document.fonts.ready.then(remeasure);
    document.fonts.addEventListener('loadingdone', remeasure);
  }
})();
